import { Select } from 'components/partials/select/select';

import type { SpecialAwardLogicCategory } from '../hooks/use-special-award-form';

const PORTAL_STYLES = (): Record<string, unknown> => ({
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
});

interface DimensionOption {
  value: string;
  label: string;
}

interface ValueOption {
  value: string | number;
  label: string;
}

export interface SpecialAwardStudentDataSourceProps {
  blockId: string;
  category: string;
  optionIds: Array<string | number>;
  categories: SpecialAwardLogicCategory[];
  onCategoryChange: (category: string) => void;
  onOptionsChange: (optionIds: Array<string | number>) => void;
  isLoading?: boolean;
}

/** The Dimension/Value(s) pair for one Student-data criteria block. */
const SpecialAwardStudentDataSource = ({
  blockId,
  category,
  optionIds,
  categories,
  onCategoryChange,
  onOptionsChange,
  isLoading = false,
}: SpecialAwardStudentDataSourceProps) => {
  const selectedCategory = categories.find(
    (item) => item.category === category
  );
  const dimensionOptions = categories.map<DimensionOption>((item) => ({
    value: item.category,
    label: item.category,
  }));
  const valueOptions = (selectedCategory?.options ?? []).map<ValueOption>(
    (option) => ({ value: option.id, label: option.name })
  );
  const selectedDimension =
    dimensionOptions.find((option) => option.value === category) ?? null;
  const selectedValues = valueOptions.filter((option) =>
    optionIds.some((id) => String(id) === String(option.value))
  );

  return (
    <div
      className="flex w-full items-end gap-3 pr-[54px]"
      data-testid="special-award-student-data-source"
      data-modal-keyboard-ignore
    >
      <div className="w-40">
        <Select<DimensionOption>
          label="Dimension"
          labelInTop
          required
          isLoading={isLoading}
          options={dimensionOptions}
          value={selectedDimension ?? undefined}
          onChange={(option) => {
            if (option && !Array.isArray(option)) {
              onCategoryChange(option.value);
            }
          }}
          placeholder="Select..."
          menuPortalTarget={document.body}
          extraStyles={PORTAL_STYLES}
          customClass="mt-0 text-md"
          extraBasicStyles={{ minHeight: 40, padding: 0 }}
          data-testid={`student-data-dimension-${blockId}`}
        />
      </div>

      {selectedDimension && (
        <div className="min-w-0 flex-1">
          <Select<ValueOption>
            label="Value(s)"
            labelInTop
            required
            isMulti
            options={valueOptions}
            value={selectedValues}
            onChange={(value) =>
              onOptionsChange(
                (Array.isArray(value) ? value : [value])
                  .filter((option): option is ValueOption => !!option)
                  .map((option) => option.value)
              )
            }
            isClearable
            placeholder="Select..."
            menuPortalTarget={document.body}
            extraStyles={PORTAL_STYLES}
            customClass="mt-0 text-md"
            extraBasicStyles={{ minHeight: 40, padding: 0 }}
            data-testid={`student-data-values-${blockId}`}
          />
        </div>
      )}
    </div>
  );
};

export default SpecialAwardStudentDataSource;
